/**
 * Утилиты для склонения русских названий с использованием библиотеки lvovich
 * Используется только для русской версии сайта
 */

import { cityIn, cityFrom, cityTo, incline } from 'lvovich'

/**
 * Склоняет название города в зависимости от падежа
 * @param {string} cityName - Название города
 * @param {string} caseType - Тип падежа:
 *   - 'nominative' (именительный): Будва, Котор
 *   - 'in'/'prepositional' (предложный): в Будве, в Которе
 *   - 'from'/'genitive' (родительный): из Будвы, из Котор
 *   - 'to'/'dative' (дательный): к Будве, к Котору
 *   - 'accusative' (винительный): в Будву, в Котор
 *   - 'instrumental' (творительный): с Будвой, рядом с Котором
 * @returns {string} Склоненное название города
 */
export function declineCityName(cityName, caseType = 'nominative') {
    if (!cityName || typeof cityName !== 'string') {
        return cityName
    }

    try {
        // Всегда храним результат здесь и в конце гарантированно возвращаем
        let result = cityName

        // Нормализуем тип падежа для внутренних функций
        let normalizedCase = caseType
        switch (caseType) {
            case 'in':
            case 'prepositional':
                normalizedCase = 'prepositional'
                result = cityIn(cityName) || cityName
                break
            case 'from':
            case 'genitive':
                normalizedCase = 'genitive'
                result = cityFrom(cityName) || cityName
                break
            case 'to':
            case 'dative':
                normalizedCase = 'dative'
                result = cityTo(cityName) || cityName
                break
            case 'accusative':
                // Винительный падеж: "в Будву", "в Котор"
                // Для неодушевленных существительных (города) винительный = именительный
                // Но для некоторых конструкций ("в Будву") используется родительный
                try {
                    // Пробуем использовать incline, если доступен
                    const acc = incline(cityName, { case: 'accusative' })
                    if (acc && acc !== cityName) {
                        result = acc
                        break
                    }
                } catch {
                    // Игнорируем ошибку
                }
                // Fallback: для городов винительный часто совпадает с родительным в конструкциях "в [город]"
                normalizedCase = 'accusative'
                result = cityFrom(cityName) || cityName
                break
            case 'instrumental':
                // Творительный падеж: "с Будвой", "рядом с Котором"
                try {
                    // Пробуем использовать incline, если доступен
                    const ins = incline(cityName, { case: 'instrumental' })
                    if (ins && ins !== cityName) {
                        result = ins
                        break
                    }
                } catch {
                    // Игнорируем ошибку
                }
                // Fallback: используем простую логику склонения
                const trimmed = cityName.trim()
                const lastChar = trimmed.slice(-1).toLowerCase()
                if (lastChar === 'а') {
                    // Женский род на -а: Будва -> Будвой
                    result = trimmed.slice(0, -1) + 'ой'
                } else if (lastChar === 'я') {
                    // Женский род на -я: Ялта -> Ялтой
                    result = trimmed.slice(0, -1) + 'ей'
                } else if (lastChar === 'й') {
                    // Мужской род на -й: Котор -> Котором
                    result = trimmed.slice(0, -1) + 'ем'
                } else if (!'аеёиоуыэюя'.includes(lastChar)) {
                    // Мужской род на согласную: Москва -> Москвой (но обычно города женского рода)
                    result = trimmed + 'ом'
                }
                normalizedCase = 'instrumental'
                break
            case 'nominative':
            default:
                normalizedCase = 'nominative'
                result = cityName // Именительный падеж - без изменений
                break
        }

        // Если результат пустой или совпадает с исходным, пробуем наше простое склонение
        if (!result || typeof result !== 'string' || result.trim() === '' || result === cityName) {
            const declined = simpleDecline(cityName, normalizedCase)
            if (declined && typeof declined === 'string' && declined.trim() !== '') {
                return declined
            }
            return cityName
        }
        return result
    } catch (error) {
        if (process.env.NODE_ENV === 'development') {
            console.warn('⚠️ Ошибка склонения города:', cityName, error)
        }
        return cityName // В случае ошибки возвращаем исходное название
    }
}

/**
 * Простое склонение русских слов по правилам
 * @param {string} word - Слово для склонения
 * @param {string} caseType - Тип падежа
 * @returns {string} Склоненное слово
 */
function simpleDecline(word, caseType) {
    if (!word || word.length < 2) return word

    const trimmed = word.trim()
    const lastChar = trimmed.slice(-1).toLowerCase()
    const lastTwoChars = trimmed.slice(-2).toLowerCase()
    const base = trimmed.slice(0, -1)
    const base2 = trimmed.slice(0, -2)

    // Прилагательные женского рода (окончания на -ая, -яя)
    if (lastTwoChars === 'ая' || lastTwoChars === 'яя') {
        switch (caseType) {
            case 'genitive': case 'from': return base2 + 'ой'
            case 'dative': case 'to': return base2 + 'ой'
            case 'accusative': return base2 + 'ую'
            case 'instrumental': return base2 + 'ой'
            case 'prepositional': case 'in': return base2 + 'ой'
            default: return trimmed
        }
    }

    // Женский род (окончания на -а, -я)
    if (lastChar === 'а') {
        // Проверяем, какая согласная перед -а
        const beforeLast = trimmed.slice(-2, -1).toLowerCase()
        // Если перед -а стоит г, к, х, ж, ч, ш, щ - используем -и, иначе -ы
        const useI = 'гкхжчшщ'.includes(beforeLast)

        switch (caseType) {
            case 'genitive': case 'from': return base + (useI ? 'и' : 'ы')
            case 'dative': case 'to': return base + 'е'
            case 'accusative': return base + 'у'
            case 'instrumental': return base + 'ой'
            case 'prepositional': case 'in': return base + 'е'
            default: return trimmed
        }
    }
    if (lastChar === 'я') {
        // Слова на -ия (Грузия, Турция, Армения): дат. и предл. → -ии, не -ие
        const endsInIya = lastTwoChars === 'ия'
        switch (caseType) {
            case 'genitive': case 'from': return base + 'и'
            case 'dative': case 'to': return base + (endsInIya ? 'и' : 'е')
            case 'accusative': return base + 'ю'
            case 'instrumental': return base + 'ей'
            case 'prepositional': case 'in': return base + (endsInIya ? 'и' : 'е')
            default: return trimmed
        }
    }

    // Мужской род (окончания на согласную, -й)
    if (lastChar === 'й') {
        switch (caseType) {
            case 'genitive': case 'from': return base + 'я'
            case 'dative': case 'to': return base + 'ю'
            case 'accusative': return base + 'й'
            case 'instrumental': return base + 'ем'
            case 'prepositional': case 'in': return base + 'е'
            default: return trimmed
        }
    }

    // Мужской род (твердая согласная)
    if (!'аеёиоуыэюя'.includes(lastChar)) {
        switch (caseType) {
            case 'genitive': case 'from': return trimmed + 'а'
            case 'dative': case 'to': return trimmed + 'у'
            case 'accusative': return trimmed
            case 'instrumental': return trimmed + 'ом'
            case 'prepositional': case 'in': return trimmed + 'е'
            default: return trimmed
        }
    }

    return trimmed
}

/**
 * Склоняет название (для POI, аэропортов и других объектов)
 * @param {string} name - Название
 * @param {string} caseType - Тип падежа: 'nominative', 'genitive', 'dative', 'accusative', 'instrumental', 'prepositional', 'in', 'from', 'to'
 * @returns {string} Склоненное название
 */
export function declineName(name, caseType = 'nominative') {
    if (!name || typeof name !== 'string') {
        return name
    }

    // Для именительного падежа возвращаем без изменений
    if (caseType === 'nominative') {
        return name
    }

    try {
        // Если название состоит из нескольких слов, склоняем первое слово
        const words = name.trim().split(/\s+/)
        if (words.length > 1) {
            // Склоняем первое слово (обычно это основное слово: "Район", "Рынок", "Набережная")
            const firstWord = words[0]
            const declinedFirstWord = simpleDecline(firstWord, caseType)
            return declinedFirstWord + ' ' + words.slice(1).join(' ')
        } else {
            // Одно слово - склоняем его
            return simpleDecline(name, caseType)
        }
    } catch (error) {
        if (process.env.NODE_ENV === 'development') {
            console.warn('⚠️ Ошибка склонения названия:', name, error)
        }
        return name // В случае ошибки возвращаем исходное название
    }
}

/**
 * Имя страны для GF-страниц + функция склонения. Был скопирован 1:1 в 5
 * *PageContent (vpn/insurance/transport/tours/esim): cName — локализованное имя
 * или капитализированный slug; cCase(caseType) — склонение для ru, passthrough для en.
 * @param {string|null} countryName — локализованное имя из данных (приоритет)
 * @param {string|null} slug — слаг страны (фолбэк, капитализируется)
 * @param {boolean} isRu
 * @returns {{ cName: string, cCase: (caseType: string) => string }}
 */
export function makeCountryDisplay(countryName, slug, isRu) {
    const cName = countryName || (slug ? slug.charAt(0).toUpperCase() + slug.slice(1) : '');
    const cCase = (caseType) => (isRu && cName ? declineName(cName, caseType) : cName);
    return { cName, cCase };
}

/**
 * Определяет тип падежа из плейсхолдера
 * Поддерживает форматы: [CITY_KOTOR], [CITY_KOTOR_IN], [CITY_KOTOR_FROM], [CITY_KOTOR_TO], 
 * [CITY_KOTOR_PREP], [CITY_KOTOR_GEN], [CITY_KOTOR_DAT], [CITY_KOTOR_ACC], [CITY_KOTOR_INS]
 * @param {string} placeholder - Плейсхолдер (например, [CITY_KOTOR_IN] или [CITY_BUDVA_PREP])
 * @returns {Object} { basePlaceholder, caseType } - Базовый плейсхолдер и тип падежа
 */
export function parsePlaceholderCase(placeholder) {
    if (!placeholder || typeof placeholder !== 'string') {
        return { basePlaceholder: placeholder, caseType: 'nominative' }
    }

    // Проверяем наличие суффиксов падежей в конце плейсхолдера
    const caseSuffixes = {
        '_IN': 'in',
        '_FROM': 'from',
        '_TO': 'to',
        '_GEN': 'genitive',
        '_DAT': 'dative',
        '_ACC': 'accusative',
        '_INS': 'instrumental',
        '_PREP': 'prepositional'
    }

    for (const [suffix, caseType] of Object.entries(caseSuffixes)) {
        if (placeholder.endsWith(suffix + ']')) {
            // Убираем суффикс падежа из плейсхолдера
            const basePlaceholder = placeholder.slice(0, -suffix.length - 1) + ']'
            return { basePlaceholder, caseType }
        }
    }

    // Если суффикса нет, используем именительный падеж
    return { basePlaceholder: placeholder, caseType: 'nominative' }
}

/**
 * Склоняет слово "Аэропорт" по падежам
 * @param {string} caseType - Тип падежа
 * @returns {string} Склоненное слово "Аэропорт"
 */
export function declineAirportWord(caseType = 'nominative') {
    switch (caseType) {
        case 'genitive':
        case 'from':
            return 'Аэропорта'
        case 'dative':
        case 'to':
            return 'Аэропорту'
        case 'accusative':
            return 'Аэропорт'
        case 'instrumental':
            return 'Аэропортом'
        case 'prepositional':
        case 'in':
            return 'Аэропорте'
        case 'nominative':
        default:
            return 'Аэропорт'
    }
}

/**
 * Формирует локализованное название аэропорта с учетом падежа
 * Для русского: "Аэропорт Цюриха", "Аэропорта Цюриха", "в Аэропорте Цюриха" и т.д.
 * Для английского: "Zurich Airport" (без падежей)
 * @param {string} cityName - Название города (например, "Цюрих" или "Zurich")
 * @param {string} caseType - Тип падежа
 * @param {string} language - Язык ('ru' или 'en')
 * @returns {string} Локализованное название аэропорта
 */
export function formatAirportName(cityName, caseType = 'nominative', language = 'ru') {
    if (!cityName) return ''

    if (language === 'en') {
        // Для английского: "Zurich Airport" (без падежей)
        return `${cityName} Airport`
    }

    // Для русского: склоняем и слово "Аэропорт", и название города
    const airportWord = declineAirportWord(caseType)
    // Название города в родительном падеже (Цюрих -> Цюриха)
    const cityGenitive = declineCityName(cityName, 'genitive')

    return `${airportWord} ${cityGenitive}`
}


/**
 * Русская плюрализация: ruPlural(5, ['шаг', 'шага', 'шагов']) → 'шагов'
 * @param {number} n - Число
 * @param {[string, string, string]} forms - Формы для 1 / 2–4 / 5–20
 * @returns {string} Подходящая форма слова
 */
export function ruPlural(n, forms) {
    const abs = Math.abs(n) % 100
    const last = abs % 10
    if (abs > 10 && abs < 20) return forms[2]
    if (last > 1 && last < 5) return forms[1]
    if (last === 1) return forms[0]
    return forms[2]
}
